/**
 * AEO Checklist Tab - Track AI Search Optimization Manual Tasks
 */
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  CheckCircle2, Circle, ExternalLink, Search, Globe, MapPin, 
  Star, FileText, RefreshCw, TrendingUp, AlertCircle, Clock,
  ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

// AEO Checklist items organized by category
const AEO_CHECKLIST = [
  {
    id: 'search-consoles',
    category: 'Search Console Submissions',
    icon: Search,
    description: 'Submit your sitemap to search engines for indexing',
    items: [
      {
        id: 'google-search-console',
        title: 'Submit to Google Search Console',
        description: 'Add your site and submit sitemap.xml for Google indexing',
        url: 'https://search.google.com/search-console',
        priority: 'high',
        steps: [
          'Go to Google Search Console',
          'Add property: bulaccinocafe.com',
          'Verify ownership (HTML file or DNS)',
          'Go to Sitemaps section',
          'Submit: https://bulaccinocafe.com/sitemap.xml'
        ]
      },
      {
        id: 'bing-webmaster',
        title: 'Submit to Bing Webmaster Tools',
        description: 'Critical for ChatGPT visibility - Bing powers ChatGPT search',
        url: 'https://www.bing.com/webmasters',
        priority: 'high',
        steps: [
          'Go to Bing Webmaster Tools',
          'Sign in with Microsoft account',
          'Add site: bulaccinocafe.com',
          'Verify ownership',
          'Submit sitemap: https://bulaccinocafe.com/sitemap.xml',
          'Use URL Inspection to request indexing for key pages'
        ]
      }
    ]
  },
  {
    id: 'business-listings',
    category: 'Business Listings',
    icon: MapPin,
    description: 'Claim and optimize your business on major platforms',
    items: [
      {
        id: 'google-business',
        title: 'Google Business Profile',
        description: 'Essential for local search and Google AI',
        url: 'https://business.google.com',
        priority: 'high',
        steps: [
          'Claim or create your business listing',
          'Ensure name, address, phone match website exactly',
          'Add business category: Cafe, Coffee Shop, Restaurant',
          'Upload 10+ high-quality photos',
          'Add your menu',
          'Enable messaging and Q&A',
          'Post weekly updates'
        ]
      },
      {
        id: 'tripadvisor',
        title: 'TripAdvisor Listing',
        description: 'Critical for tourism AI queries about Fiji',
        url: 'https://www.tripadvisor.com/Owners',
        priority: 'high',
        steps: [
          'Go to TripAdvisor for Business',
          'Search for your business or add new',
          'Claim ownership and verify',
          'Complete all profile sections',
          'Add menu and photos',
          'Respond to all reviews',
          'Keep hours and info updated'
        ]
      },
      {
        id: 'yelp',
        title: 'Yelp Business',
        description: 'Popular review platform indexed by AI',
        url: 'https://biz.yelp.com',
        priority: 'medium',
        steps: [
          'Claim your business on Yelp',
          'Complete all profile information',
          'Add photos and menu',
          'Respond to reviews'
        ]
      },
      {
        id: 'facebook',
        title: 'Facebook Business Page',
        description: 'Social presence helps AI credibility',
        url: 'https://business.facebook.com',
        priority: 'medium',
        steps: [
          'Create or update Facebook Business Page',
          'Ensure info matches website',
          'Enable reviews',
          'Post regularly'
        ]
      }
    ]
  },
  {
    id: 'tourism-listings',
    category: 'Tourism & Travel Sites',
    icon: Globe,
    description: 'Get listed on Fiji tourism resources',
    items: [
      {
        id: 'tourism-fiji',
        title: 'Tourism Fiji Directory',
        description: 'Official Fiji tourism website - high authority',
        url: 'https://www.fiji.travel',
        priority: 'high',
        steps: [
          'Contact Tourism Fiji',
          'Request listing in their dining directory',
          'Provide high-quality images',
          'Include full business description'
        ]
      },
      {
        id: 'lonely-planet',
        title: 'Lonely Planet',
        description: 'Major travel guide platform',
        url: 'https://www.lonelyplanet.com/fiji',
        priority: 'medium',
        steps: [
          'Check if already listed',
          'Submit business for consideration',
          'Engage with user reviews if listed'
        ]
      }
    ]
  },
  {
    id: 'reviews',
    category: 'Review Generation',
    icon: Star,
    description: 'Collect reviews to boost AI credibility',
    items: [
      {
        id: 'google-reviews',
        title: 'Get 10+ Google Reviews',
        description: 'Google reviews heavily influence AI answers',
        url: null,
        priority: 'high',
        steps: [
          'Create a short review link from Google Business',
          'Ask satisfied customers to leave reviews',
          'Add review request to receipts/email confirmations',
          'Respond to all reviews professionally',
          'Aim for 10+ reviews with 4.5+ average'
        ]
      },
      {
        id: 'tripadvisor-reviews',
        title: 'Get TripAdvisor Reviews',
        description: 'Tourism AI queries rely heavily on TripAdvisor',
        url: null,
        priority: 'high',
        steps: [
          'Encourage tourists to review on TripAdvisor',
          'Add TripAdvisor widget to your website',
          'Respond to all reviews',
          'Share positive reviews on social media'
        ]
      }
    ]
  },
  {
    id: 'content',
    category: 'Content & Backlinks',
    icon: FileText,
    description: 'Create content and get mentioned on other sites',
    items: [
      {
        id: 'blog-posts',
        title: 'Publish Blog Posts',
        description: 'AI-optimized content targeting Fiji cafe queries',
        url: '/admin?tab=blog',
        priority: 'medium',
        steps: [
          'Write "Best Coffee in Fiji" article',
          'Write "Fiji Breakfast Guide" article',
          'Include internal links to menu',
          'Share on social media'
        ]
      },
      {
        id: 'travel-blogs',
        title: 'Get Featured on Travel Blogs',
        description: 'Backlinks from travel bloggers boost AI visibility',
        url: null,
        priority: 'medium',
        steps: [
          'Identify Fiji travel bloggers',
          'Offer complimentary experience for review',
          'Request backlink to your website',
          'Share their content on your social'
        ]
      }
    ]
  },
  {
    id: 'monitoring',
    category: 'Monitoring & Testing',
    icon: TrendingUp,
    description: 'Track your AI search visibility',
    items: [
      {
        id: 'test-chatgpt',
        title: 'Test ChatGPT Visibility',
        description: 'Periodically ask ChatGPT about Fiji cafes',
        url: 'https://chat.openai.com',
        priority: 'low',
        steps: [
          'Ask: "What is the best cafe in Fiji?"',
          'Ask: "Where to get coffee in Nadi?"',
          'Ask: "Best breakfast in Fiji"',
          'Note if Bulaccino is mentioned',
          'Repeat monthly to track progress'
        ]
      },
      {
        id: 'test-perplexity',
        title: 'Test Perplexity AI',
        description: 'Check visibility on Perplexity search',
        url: 'https://www.perplexity.ai',
        priority: 'low',
        steps: [
          'Search same queries on Perplexity',
          'Check if site is cited in answers',
          'Note improvements over time'
        ]
      },
      {
        id: 'schema-validation',
        title: 'Validate Structured Data',
        description: 'Ensure schemas are properly implemented',
        url: 'https://search.google.com/test/rich-results',
        priority: 'medium',
        steps: [
          'Test homepage URL',
          'Test FAQ page URL',
          'Fix any errors reported',
          'Re-test after fixes'
        ]
      }
    ]
  }
];

function ChecklistItem({ item, isCompleted, onToggle, isExpanded, onExpand }) {
  const priorityColors = {
    high: 'text-red-500 bg-red-50 dark:bg-red-950/30',
    medium: 'text-amber-500 bg-amber-50 dark:bg-amber-950/30',
    low: 'text-blue-500 bg-blue-50 dark:bg-blue-950/30'
  };

  return (
    <div className={`border rounded-xl overflow-hidden transition-all ${
      isCompleted ? 'border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-950/20' : 'border-border'
    }`}>
      <div className="p-4">
        <div className="flex items-start gap-3">
          <button
            onClick={onToggle}
            className="mt-0.5 flex-shrink-0"
            data-testid={`toggle-${item.id}`}
          >
            {isCompleted ? (
              <CheckCircle2 className="w-6 h-6 text-green-500" />
            ) : (
              <Circle className="w-6 h-6 text-muted-foreground hover:text-primary transition-colors" />
            )}
          </button>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className={`font-medium ${isCompleted ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                {item.title}
              </h4>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${priorityColors[item.priority]}`}>
                {item.priority}
              </span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
            
            <div className="flex items-center gap-3 mt-3">
              {item.url && (
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary hover:underline flex items-center gap-1"
                >
                  <ExternalLink className="w-3 h-3" />
                  Open
                </a>
              )}
              <button
                onClick={onExpand}
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
              >
                {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {isExpanded ? 'Hide steps' : 'Show steps'}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {isExpanded && item.steps && (
        <div className="px-4 pb-4 pt-0 ml-9">
          <ol className="list-decimal list-inside space-y-1.5 text-sm text-muted-foreground">
            {item.steps.map((step, idx) => (
              <li key={idx} className="leading-relaxed">{step}</li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

function CategorySection({ category, completedItems, onToggleItem, expandedItems, onExpandItem }) {
  const Icon = category.icon;
  const completedCount = category.items.filter(item => completedItems.includes(item.id)).length;
  const totalCount = category.items.length;
  const progress = (completedCount / totalCount) * 100;

  return (
    <div className="mb-8">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Icon className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading text-lg">{category.category}</h3>
          <p className="text-sm text-muted-foreground">{category.description}</p>
        </div>
        <div className="text-right">
          <span className="text-sm font-medium text-foreground">{completedCount}/{totalCount}</span>
          <div className="w-24 h-2 bg-muted rounded-full overflow-hidden mt-1">
            <div 
              className="h-full bg-primary rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>
      
      <div className="space-y-3">
        {category.items.map(item => (
          <ChecklistItem
            key={item.id}
            item={item}
            isCompleted={completedItems.includes(item.id)}
            onToggle={() => onToggleItem(item.id)}
            isExpanded={expandedItems.includes(item.id)}
            onExpand={() => onExpandItem(item.id)}
          />
        ))}
      </div>
    </div>
  );
}

export default function AeoChecklistTab() {
  const [completedItems, setCompletedItems] = useState([]);
  const [expandedItems, setExpandedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Fetch saved progress
  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const res = await api.get('/admin/aeo-checklist');
        setCompletedItems(res.data.completed_items || []);
      } catch (err) {
        console.error('Failed to fetch AEO checklist progress:', err);
      }
      setLoading(false);
    };
    fetchProgress();
  }, []);

  // Save progress
  const saveProgress = async (items) => {
    setSaving(true);
    try {
      await api.put('/admin/aeo-checklist', { completed_items: items });
    } catch (err) {
      console.error('Failed to save AEO checklist progress:', err);
    }
    setSaving(false);
  };

  const handleToggleItem = (itemId) => {
    setCompletedItems(prev => {
      const newItems = prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId];
      saveProgress(newItems);
      
      if (!prev.includes(itemId)) {
        toast.success('Task marked as complete!');
      }
      return newItems;
    });
  };

  const handleExpandItem = (itemId) => {
    setExpandedItems(prev => 
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  // Calculate overall progress
  const totalItems = AEO_CHECKLIST.reduce((sum, cat) => sum + cat.items.length, 0);
  const completedCount = completedItems.length;
  const overallProgress = Math.round((completedCount / totalItems) * 100);

  // Count by priority
  const highPriorityItems = AEO_CHECKLIST.flatMap(cat => cat.items).filter(i => i.priority === 'high');
  const completedHighPriority = highPriorityItems.filter(i => completedItems.includes(i.id)).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="aeo-checklist-tab">
      {/* Header */}
      <div>
        <h2 className="font-heading text-2xl mb-2">AI Search Optimization Checklist</h2>
        <p className="text-muted-foreground">
          Complete these tasks to improve your visibility in AI search results (ChatGPT, Google AI, Perplexity).
        </p>
      </div>

      {/* Progress Overview */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-2xl p-5">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              overallProgress === 100 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-primary/10'
            }`}>
              {overallProgress === 100 ? (
                <CheckCircle2 className="w-6 h-6 text-green-500" />
              ) : (
                <span className="text-xl font-bold text-primary">{overallProgress}%</span>
              )}
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Overall Progress</p>
              <p className="font-heading text-lg">{completedCount} of {totalItems} tasks</p>
            </div>
          </div>
          <div className="mt-3 h-2 bg-muted rounded-full overflow-hidden">
            <motion.div 
              className="h-full bg-primary rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${overallProgress}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>

        <div className="bg-card border border-border rounded-2xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-red-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">High Priority</p>
              <p className="font-heading text-lg">{completedHighPriority} of {highPriorityItems.length} done</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-2xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <Clock className="w-6 h-6 text-amber-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Est. Time</p>
              <p className="font-heading text-lg">~2-3 hours</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tip */}
      <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
        <div className="flex gap-3">
          <TrendingUp className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-blue-900 dark:text-blue-100">Pro Tip</p>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Focus on <strong>high priority</strong> items first - especially Bing Webmaster Tools (powers ChatGPT) 
              and TripAdvisor (tourism AI queries). Changes may take 2-4 weeks to reflect in AI search results.
            </p>
          </div>
        </div>
      </div>

      {/* Saving indicator */}
      {saving && (
        <div className="fixed bottom-4 right-4 bg-card border border-border rounded-lg px-4 py-2 shadow-lg flex items-center gap-2">
          <RefreshCw className="w-4 h-4 animate-spin text-primary" />
          <span className="text-sm">Saving...</span>
        </div>
      )}

      {/* Checklist Categories */}
      <div className="mt-8">
        {AEO_CHECKLIST.map(category => (
          <CategorySection
            key={category.id}
            category={category}
            completedItems={completedItems}
            onToggleItem={handleToggleItem}
            expandedItems={expandedItems}
            onExpandItem={handleExpandItem}
          />
        ))}
      </div>

      {/* Completion Message */}
      {overallProgress === 100 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-xl p-6 text-center"
        >
          <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <h3 className="font-heading text-xl text-green-800 dark:text-green-200">All Tasks Complete!</h3>
          <p className="text-green-700 dark:text-green-300 mt-2">
            Excellent work! Your AI search optimization is complete. Check back in 2-4 weeks 
            to test your visibility on ChatGPT and other AI search engines.
          </p>
        </motion.div>
      )}
    </div>
  );
}
